import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/auth/PrivateRoute';
import RouteGuard from './components/auth/RouteGuard';
import LoginForm from './components/auth/LoginForm';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ChangePassword from './components/auth/ChangePassword';
import Navbar from './components/Navbar';
import {
  Box,
  CircularProgress,
  Toolbar
} from '@mui/material';
import { getAllowedTypes, SYSTEM_CONSOLE_DEFAULT_PATH } from './config/routes';
import { RepairsProvider } from './contexts/RepairsContext';
import { DocumentStatusesProvider, preloadDocumentStatuses } from './contexts/DocumentStatusesContext';
// Lazy loading для всех компонентов страниц
const ServiceCentersList = lazy(() => import('./components/public/ServiceCentersList'));
const Search = lazy(() => import('./components/search/Search'));
const RepairsList = lazy(() => import('./components/repairs/RepairsList'));
const RepairDetails = lazy(() => import('./components/repairs/RepairDetails'));
const RepairForm = lazy(() => import('./components/repairs/RepairForm'));
const RepairDocumentForm = lazy(() => import('./components/repairs/components/RepairDocumentForm'));

const ComingsList = lazy(() => import('./components/comings/ComingsList'));
const _ComingDetails = lazy(() => import('./components/comings/ComingDetails'));
const ComingForm = lazy(() => import('./components/comings/ComingForm'));

const OrdersList = lazy(() => import('./components/orders/OrdersList'));
const OrderForm = lazy(() => import('./components/orders/OrderForm'));
const OrdersSearch = lazy(() => import('./components/orders/OrdersSearch'));

const CustomerOrdersList = lazy(() => import('./components/customer-orders/CustomerOrdersList'));
const _CustomerOrderForm = lazy(() => import('./components/customer-orders/CustomerOrderForm'));
const CustomerOrderDetails = lazy(() => import('./components/customer-orders/CustomerOrderDetails'));
const CustomerOrdersSearch = lazy(() => import('./components/customer-orders/CustomerOrdersSearch'));

const SalesList = lazy(() => import('./components/sales/SalesList'));
const SaleDetails = lazy(() => import('./components/sales/SaleDetails'));
const SalesSearch = lazy(() => import('./components/sales/SalesSearch'));

const ReturnsList = lazy(() => import('./components/returns/ReturnsList'));
const ReturnForm = lazy(() => import('./components/returns/ReturnForm'));
const ReturnDetails = lazy(() => import('./components/returns/ReturnDetails'));
const ReturnsSearch = lazy(() => import('./components/returns/ReturnsSearch'));

const DocumentsPage = lazy(() => import('./components/documents/DocumentsPage'));
const NewDocumentPage = lazy(() => import('./components/documents/NewDocumentPage'));
const DocumentViewer = lazy(() => import('./components/documents/DocumentViewer'));
const EditDocumentPage = lazy(() => import('./components/documents/EditDocumentPage'));
const DocumentPrintView = lazy(() => import('./components/documents/DocumentPrintView'));

const ModelsList = lazy(() => import('./components/references/models/ModelsList'));
const ModelForm = lazy(() => import('./components/references/models/ModelForm'));
const ModelDetail = lazy(() => import('./components/references/models/ModelDetail'));
const CategoriesPage = lazy(() => import('./components/references/categories/CategoriesPage'));
const SparesList = lazy(() => import('./components/references/spares/SparesList'));
const SpareForm = lazy(() => import('./components/references/spares/SpareForm'));
const WorksList = lazy(() => import('./components/references/works/WorksList'));
const WorkForm = lazy(() => import('./components/references/works/WorkForm'));

const ProfilesList = lazy(() => import('./components/references/profiles/ProfilesList'));
const ClientDedupPage = lazy(() => import('./components/references/profiles/ClientDedupPage'));
const ProfileForm = lazy(() => import('./components/references/profiles/ProfileForm'));
const ProfileCreateStep1 = lazy(() => import('./components/references/profiles/ProfileCreateStep1'));
const ProfileCompleteStep2 = lazy(() => import('./components/references/profiles/ProfileCompleteStep2'));
const ProfileDetails = lazy(() => import('./components/references/profiles/ProfileDetails'));

const GroupedReports = lazy(() => import('./components/reports/GroupedReports'));
const ReportsList = lazy(() => import('./components/reports/ReportsList'));

const MessagesList = lazy(() => import('./components/messages/MessagesList'));
const SentMessages = lazy(() => import('./components/messages/SentMessages'));
const MessageForm = lazy(() => import('./components/messages/MessageForm'));
const MessageView = lazy(() => import('./components/messages/MessageView'));
const ThreadView = lazy(() => import('./components/messages/ThreadView'));

const NotificationsPage = lazy(() => import('./components/notifications/NotificationsPage'));
const _NotificationsList = lazy(() => import('./components/notifications/NotificationsList'));
const NotificationTestPage = lazy(() => import('./components/notifications/NotificationTestPage'));
const NotificationQueueAdmin = lazy(() => import('./components/notifications/admin/NotificationQueueAdmin'));
const NotificationTemplatesAdmin = lazy(() => import('./components/notifications/admin/NotificationTemplatesAdmin'));

const Settings = lazy(() => import('./components/settings/Settings'));
const CustomerSettings = lazy(() => import('./components/customer/CustomerSettings'));

const SystemConsoleShell = lazy(() => import('./admin/system/SystemConsoleShell'));

const NotFound = lazy(() => import('./components/NotFound'));
const Forbidden = lazy(() => import('./components/Forbidden'));

// Loader для lazy компонентов
function PageLoader() {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: 'calc(100vh - 100px)',
      py: 4
    }}>
      <CircularProgress />
    </Box>
  );
}

// Wrapper для lazy компонентов с RouteGuard (componentProps — опциональные пропсы для Component)
function LazyRoute({ Component, allowedTypes, componentProps, requiredPermissions }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <RouteGuard allowedTypes={allowedTypes} requiredPermissions={requiredPermissions || []}>
        <Component {...(componentProps || {})} />
      </RouteGuard>
    </Suspense>
  );
}

function HomeRedirect() {
  const { user } = useAuth();

  if (user?.profile_type_code === 'system_console') {
    return <Navigate to={SYSTEM_CONSOLE_DEFAULT_PATH} replace />;
  }
  if (user?.profile_type_code === 'company') {
    return <Navigate to="/repairs/current" replace />;
  }
  if (user?.profile_type_code === 'depot') {
    return <Navigate to="/repairs" replace />;
  }
  if (user?.profile_type_code === 'customer') {
    return <Navigate to="/repairs" replace />;
  }
  return <Navigate to="/orders" replace />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user) {
    if (user.must_change_password) {
      return <Navigate to="/change-password" replace />;
    }
    if (user.profile_type_code === 'system_console') {
      return <Navigate to={SYSTEM_CONSOLE_DEFAULT_PATH} replace />;
    }
    return <Navigate to="/" replace />;
  }
  return <LoginForm />;
}

function AppRoutes() {
  const { user } = useAuth();
  
  // Preload статусов при инициализации приложения (когда пользователь авторизован)
  useEffect(() => {
    if (user) {
      preloadDocumentStatuses()
        .catch(err => console.error('Failed to preload document statuses:', err));
    }
  }, [user]);
  
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      
      {/* Публичные маршруты (без авторизации) */}
      <Route path="/public/service-centers" element={<Suspense fallback={<PageLoader />}><ServiceCentersList /></Suspense>} />
      
      <Route element={<PrivateRoute />}>
        <Route path="/change-password" element={<ChangePassword />} />
        <Route
          element={
            <DocumentStatusesProvider>
              <div className="app">
                <Box
                  component="a"
                  href="#app-main"
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: 8,
                    zIndex: 2000,
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 1,
                    boxShadow: 2,
                    transform: 'translateY(-200%)',
                    '&:focus-visible': {
                      transform: 'translateY(0)',
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                    },
                  }}
                >
                  Перейти к содержимому
                </Box>
                <Navbar />
                <Box
                  component="main"
                  id="app-main"
                  tabIndex={-1}
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: '100%',
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Toolbar
                    sx={{
                      // гарантируем корректный отступ под фиксированный AppBar
                      minHeight: { xs: 56, sm: 64 },
                      mb: 1,
                    }}
                  />
                  <Outlet />
                </Box>
              </div>
            </DocumentStatusesProvider>
          }
        >
          {/* Домашняя страница */}
          <Route path="/" element={<HomeRedirect />} />
          
          {/* Поиск */}
          <Route path="/search" element={<LazyRoute Component={Search} allowedTypes={getAllowedTypes('/search')} />} />
          
          {/* Ремонты */}
          <Route path="/repairs">
            <Route index element={<Navigate to="current" replace />} />
            <Route element={<RepairsProvider><Outlet /></RepairsProvider>}>
              <Route path="current" element={<LazyRoute Component={RepairsList} allowedTypes={getAllowedTypes('/repairs')} />} />
              <Route path="new" element={<LazyRoute Component={RepairForm} allowedTypes={getAllowedTypes('/repairs/new')} />} />
              <Route path=":id" element={<LazyRoute Component={RepairDetails} allowedTypes={getAllowedTypes('/repairs/:id')} />} />
              <Route path="documents/new" element={<LazyRoute Component={RepairDocumentForm} allowedTypes={getAllowedTypes('/repairs/new')} />} />
              <Route path="documents/:id/edit" element={<LazyRoute Component={RepairDocumentForm} allowedTypes={getAllowedTypes('/repairs/:id/edit')} />} />
            </Route>
          </Route>

          {/* Закупки */}
          <Route path="/comings">
            <Route index element={<LazyRoute Component={ComingsList} allowedTypes={getAllowedTypes('/comings')} />} />
            <Route path="new" element={<LazyRoute Component={ComingForm} allowedTypes={getAllowedTypes('/comings/new')} componentProps={{ mode: 'create' }} />} />
            <Route path=":id" element={<LazyRoute Component={ComingForm} allowedTypes={getAllowedTypes('/comings/:id')} componentProps={{ mode: 'view' }} />} />
            <Route path=":id/edit" element={<LazyRoute Component={ComingForm} allowedTypes={getAllowedTypes('/comings/:id/edit')} componentProps={{ mode: 'edit' }} />} />
          </Route>
          
          {/* Заказы клиентов */}
          <Route path="/customer-orders">
            <Route index element={<LazyRoute Component={CustomerOrdersList} allowedTypes={getAllowedTypes('/customer-orders')} />} />
            <Route path="new" element={<LazyRoute Component={CustomerOrderDetails} allowedTypes={getAllowedTypes('/customer-orders/new')} componentProps={{ createMode: true }} />} />
            <Route path=":id" element={<LazyRoute Component={CustomerOrderDetails} allowedTypes={getAllowedTypes('/customer-orders/:id')} />} />
            <Route path=":id/edit" element={<LazyRoute Component={CustomerOrderDetails} allowedTypes={getAllowedTypes('/customer-orders/:id/edit')} />} />
            <Route path="search" element={<LazyRoute Component={CustomerOrdersSearch} allowedTypes={getAllowedTypes('/customer-orders/search')} />} />
          </Route>
          
          {/* Документы */}
          <Route path="/documents">
            <Route index element={<LazyRoute Component={DocumentsPage} allowedTypes={getAllowedTypes('/documents')} />} />
            <Route path="new" element={<LazyRoute Component={NewDocumentPage} allowedTypes={getAllowedTypes('/documents/new')} />} />
            <Route path=":id" element={<LazyRoute Component={DocumentViewer} allowedTypes={getAllowedTypes('/documents/:id')} />} />
            <Route path=":id/edit" element={<LazyRoute Component={EditDocumentPage} allowedTypes={getAllowedTypes('/documents/:id/edit')} />} />
            <Route path=":id/print" element={<LazyRoute Component={DocumentPrintView} allowedTypes={getAllowedTypes('/documents/:id/print')} />} />
          </Route>
          
          {/* Продажи */}
          <Route path="/sales">
            <Route index element={<LazyRoute Component={SalesList} allowedTypes={getAllowedTypes('/sales')} />} />
            <Route path="new" element={<LazyRoute Component={SaleDetails} allowedTypes={getAllowedTypes('/sales/new')} componentProps={{ createMode: true }} />} />
            <Route path=":id" element={<LazyRoute Component={SaleDetails} allowedTypes={getAllowedTypes('/sales/:id')} />} />
            <Route path=":id/edit" element={<LazyRoute Component={SaleDetails} allowedTypes={getAllowedTypes('/sales/:id/edit')} />} />
            <Route path="search" element={<LazyRoute Component={SalesSearch} allowedTypes={getAllowedTypes('/sales/search')} />} />
          </Route>
          
          {/* Заказы */}
          <Route path="/orders">
            <Route index element={<LazyRoute Component={OrdersList} allowedTypes={getAllowedTypes('/orders')} />} />
            <Route path="new" element={<LazyRoute Component={OrderForm} allowedTypes={getAllowedTypes('/orders/new')} componentProps={{ mode: 'create' }} />} />
            <Route path=":id" element={<LazyRoute Component={OrderForm} allowedTypes={getAllowedTypes('/orders/:id')} componentProps={{ mode: 'view' }} />} />
            <Route path=":id/edit" element={<LazyRoute Component={OrderForm} allowedTypes={getAllowedTypes('/orders/:id/edit')} componentProps={{ mode: 'edit' }} />} />
            <Route path="search" element={<LazyRoute Component={OrdersSearch} allowedTypes={getAllowedTypes('/orders/search')} />} />
          </Route>
          
          {/* Справочники */}
          <Route path="/references">
            <Route path="models">
              <Route index element={<LazyRoute Component={ModelsList} allowedTypes={getAllowedTypes('/references/models')} />} />
              <Route path="new" element={<LazyRoute Component={ModelForm} allowedTypes={getAllowedTypes('/references/models/new')} />} />
              <Route path=":uid" element={<LazyRoute Component={ModelDetail} allowedTypes={getAllowedTypes('/references/models/:uid')} />} />
            </Route>
            <Route path="categories">
              <Route index element={<LazyRoute Component={CategoriesPage} allowedTypes={getAllowedTypes('/references/categories')} />} />
              <Route path=":uid" element={<LazyRoute Component={CategoriesPage} allowedTypes={getAllowedTypes('/references/categories/:uid')} />} />
            </Route>
            <Route path="spares">
              <Route index element={<LazyRoute Component={SparesList} allowedTypes={getAllowedTypes('/references/spares')} />} />
              <Route path="new" element={<LazyRoute Component={SpareForm} allowedTypes={getAllowedTypes('/references/spares/new')} />} />
              <Route path=":uid/edit" element={<LazyRoute Component={SpareForm} allowedTypes={getAllowedTypes('/references/spares/:uid/edit')} />} />
            </Route>
            <Route path="works">
              <Route index element={<LazyRoute Component={WorksList} allowedTypes={getAllowedTypes('/references/works')} />} />
              <Route path="new" element={<LazyRoute Component={WorkForm} allowedTypes={getAllowedTypes('/references/works/new')} />} />
              <Route path=":uid/edit" element={<LazyRoute Component={WorkForm} allowedTypes={getAllowedTypes('/references/works/:uid/edit')} />} />
            </Route>
          </Route>
          
          {/* Профили */}
          <Route path="/profiles">
            <Route index element={<LazyRoute Component={ProfilesList} allowedTypes={getAllowedTypes('/profiles')} />} />
            <Route path="dedup" element={<LazyRoute Component={ClientDedupPage} allowedTypes={getAllowedTypes('/profiles/dedup')} />} />
            <Route path="new" element={<LazyRoute Component={ProfileCreateStep1} allowedTypes={getAllowedTypes('/profiles/new')} />} />
            <Route path=":uid/complete" element={<LazyRoute Component={ProfileCompleteStep2} allowedTypes={getAllowedTypes('/profiles/:uid/complete')} />} />
            <Route path=":uid" element={<LazyRoute Component={ProfileDetails} allowedTypes={getAllowedTypes('/profiles/:uid')} />} />
            <Route path=":uid/edit" element={<LazyRoute Component={ProfileForm} allowedTypes={getAllowedTypes('/profiles/:uid/edit')} />} />
          </Route>
          
          {/* Отчеты */}
          <Route path="/reports">
            <Route index element={<LazyRoute Component={GroupedReports} allowedTypes={getAllowedTypes('/reports')} />} />
            <Route path="list" element={<LazyRoute Component={ReportsList} allowedTypes={getAllowedTypes('/reports/list')} />} />
          </Route>
          
          {/* Сообщения */}
          <Route path="/messages">
            <Route path="inbox" element={<LazyRoute Component={MessagesList} allowedTypes={getAllowedTypes('/messages/inbox')} />} />
            <Route path="sent" element={<LazyRoute Component={SentMessages} allowedTypes={getAllowedTypes('/messages/sent')} />} />
            <Route path="new" element={<LazyRoute Component={MessageForm} allowedTypes={getAllowedTypes('/messages/new')} />} />
            <Route path="thread/:messageUid" element={<LazyRoute Component={ThreadView} allowedTypes={getAllowedTypes('/messages/:id')} />} />
            <Route path=":messageUid" element={<LazyRoute Component={MessageView} allowedTypes={getAllowedTypes('/messages/:id')} />} />
            <Route path=":messageUid/edit" element={<LazyRoute Component={MessageForm} allowedTypes={getAllowedTypes('/messages/:id/edit')} />} />
          </Route>
          
          {/* Уведомления */}
          <Route path="/notifications">
            <Route index element={<LazyRoute Component={NotificationsPage} allowedTypes={getAllowedTypes('/notifications')} />} />
            <Route path="test" element={<LazyRoute Component={NotificationTestPage} allowedTypes={getAllowedTypes('/notifications/test')} />} />
            <Route path="admin/queue" element={<LazyRoute Component={NotificationQueueAdmin} allowedTypes={getAllowedTypes('/notifications/admin/queue')} />} />
            <Route path="admin/templates" element={<LazyRoute Component={NotificationTemplatesAdmin} allowedTypes={getAllowedTypes('/notifications/admin/templates')} />} />
          </Route>
          
          {/*
            Системная консоль: и корень `/admin/system`, и подпути идут в один Shell.
            Shell сам отправит на первый доступный модуль. Доступ гейтится permission'ом
            `system_console`, тип профиля значения не имеет (allowedTypes=[]).
          */}
          <Route
            path="/admin/system/*"
            element={(
              <LazyRoute
                Component={SystemConsoleShell}
                allowedTypes={[]}
                requiredPermissions={['system_console']}
              />
            )}
          />

          {/* Настройки */}
          <Route path="/settings" element={<LazyRoute Component={Settings} allowedTypes={getAllowedTypes('/settings')} />} />
          
          {/* Настройки пользователя (старый роут для совместимости) */}
          <Route path="/customer/settings" element={<LazyRoute Component={CustomerSettings} allowedTypes={getAllowedTypes('/customer/settings')} />} />
          
          {/* Возвраты */}
          <Route path="/returns">
            <Route index element={<LazyRoute Component={ReturnsList} allowedTypes={getAllowedTypes('/returns')} />} />
            <Route path="new" element={<LazyRoute Component={ReturnForm} allowedTypes={getAllowedTypes('/returns/new')} />} />
            <Route path=":id" element={<LazyRoute Component={ReturnDetails} allowedTypes={getAllowedTypes('/returns/:id')} />} />
            <Route path=":id/edit" element={<LazyRoute Component={ReturnForm} allowedTypes={getAllowedTypes('/returns/:id/edit')} />} />
            <Route path="search" element={<LazyRoute Component={ReturnsSearch} allowedTypes={getAllowedTypes('/returns/search')} />} />
          </Route>
          
          {/* 403 */}
          <Route path="/403" element={<Suspense fallback={<PageLoader />}><Forbidden /></Suspense>} />
          
          {/* 404 */}
          <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
        </Route>
      </Route>
    </Routes>
  );
}

// Граница HMR: при изменении лениво подгружаемых модулей перезагружаем Routes,
// чтобы не вызывать import() из уже выгруженного (disposed) модуля
if (module.hot) {
  module.hot.accept();
}

export default AppRoutes; 